<template>
  <div class="configuration">
    <div v-if="showIdentityManagementPanel">
      <div
        :class="$style.sectionRoot"
      >
        <header
          :class="[
            $style.sectionHeader,
            $style.sectionGeneralMargin,
          ]"
        >
          Identity Management
        </header>
        <p
          data-test-id="idmDescription"
        >
          {{ getIdmDescription }}

          <Link
            v-if="hasLearnMore(item.name)"
            :href="getScimLearnMore(item.name)"
            isExternal
            target="_blank"
            :text="learnMoreText"
          />
        </p>

        <div
          v-if="isAppScimBeta()"
          :class="['beta-tag', $style.infoContainer]"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 27 10" v-bind:class="'beta-img'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M5.522 5.383h-1.56v1.8h1.56a1.434 1.434 0 00.874-.214.796.796 0 00.277-.673.834.834 0 00-.277-.691 1.395 1.395 0 00-.874-.222zM6.52 3.67a.752.752 0 00-.282-.64 1.425 1.425 0 00-.86-.213H3.962v1.706h1.415q1.142 0 1.142-.852z" fill="#353f44" fill-rule="evenodd"/><path d="M25.744 0H1.256A1.256 1.256 0 000 1.256v7.488A1.256 1.256 0 001.256 10h24.488A1.256 1.256 0 0027 8.744V1.256A1.256 1.256 0 0025.744 0zM7.171 7.596a2.394 2.394 0 01-1.53.439H2.888v-6.07h2.668a2.323 2.323 0 011.474.423 1.397 1.397 0 01.537 1.163 1.4 1.4 0 01-.25.831 1.425 1.425 0 01-.695.516 1.506 1.506 0 01.813.536 1.541 1.541 0 01.286.947 1.468 1.468 0 01-.55 1.215zm5.367-4.76H9.555v1.679h2.804v.868H9.555v1.782h2.983v.87H8.481v-6.07h4.057zm3.843 5.199h-1.108V2.87h-1.978v-.905h5.072v.905h-1.986zm6.63 0l-.604-1.424h-2.966l-.614 1.423h-1.108l2.745-6.069h.895l2.753 6.07z" fill="#353f44" fill-rule="evenodd"/><path fill="#353f44" fill-rule="evenodd" d="M19.807 5.759h2.234l-1.117-2.6-1.117 2.6z"/></svg>
          <p
            :class="$style.infoBetaScim"
          >
            We're evaluating Beta connectors in various real-world environments
            so we can gather feedback to enhance their performance.
          </p>
        </div>

        <hr
          v-if="showHorizontalRule"
          :class="$style.hr"
        >

        <div
          v-if="showManualImportButton"
          id="hide"
        >
          <ScimUsersImportSection />
        </div>

        <!-- Groups checkbox -->
        <div v-if="showGroupsSupport">
          <header
            :class="$style.subHeader"
          >
            Groups Management
          </header>
          <div :class="$style.groups">
            <CheckboxWithLabel
              :handleChange="setScimGroupsEnable"
              label="Enable management of User Groups and Group Membership in this application"
              :preventDefault="true"
              :value="isGroupsEnabled"
            />
          </div>
        </div>

        <div v-if="showAttributeMapping">
          <ScimConnectorAttributes
            :showAddAttributeButton="false"
          />
        </div>
      </div>

      <IdmUsersImportFilterSection v-if="useNewUsersImportFlow" />

      <ValidationObserver
        ref="credentialsForm"
        v-slot="{invalid}"
        :disabled="true"
      >
        <ApplicationCollapsibleSection
          :disableCollapse="onConfigureState"
          :expanded="showSettings"
          sectionTitle="Configuration Settings"
          @click="toggleCollapse"
        >
          <template v-slot:buttons>
            <Button
              v-if="showActivateButton "
              :data-test-id="$testId('actionButton')"
              :hasNoBackground="false"
              :isDisabled="!onConfigureState && (!showSettings || invalid)"
              :isLoading="loading"
              :text="buttonText"
              textCase="capitalize"
              @click="handleActionButton"
            />
            <ButtonSecondary
              v-if="showUpdateButton"
              :data-test-id="$testId('updateButton')"
              :isDisabled="isScimActive && (!showSettings || invalid)"
              :isLoading="loading"
              :text="buttonText"
              textCase="capitalize"
              @click="handleActionButton"
            />
          </template>
          <div :class="$style.collapsibleContent">
            <HeadingMedium
              element="h3"
              hasMargin
              text="Authentication"
              weight="bold"
            >
              Authentication
            </HeadingMedium>
            <IdmPrebuiltCredentialsForm
              :baseUrl="baseUrl"
              :clientId="clientId"
              :clientSecret="clientSecret"
              :needsVanityUrl="needsVanityUrl"
              :showApiKeyAuth="needsCredentials"
              :showClientAuth="needsSimpleToken"
              :tokenKey="tokenKey"
              @baseUrlInput="(value) => {handleCredentialsChange('baseUrl', value)}"
              @clientIdInput="(value) => {handleCredentialsChange('clientId', value)}"
              @clientSecretInput="(value) => {handleCredentialsChange('clientSecret', value)}"
              @tokenKeyInput="(value) => {handleCredentialsChange('tokenKey', value)}"
            />
          </div>
        </ApplicationCollapsibleSection>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import {
  Button, ButtonSecondary, HeadingMedium, Link,
} from '@jumpcloud/ui-components';
import { ValidationObserver } from 'vee-validate3';
import { applicationMessages, scimConnectionTypes } from 'client/util/Constants/ApplicationConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import ApplicationCollapsibleSection from 'client/applications/ApplicationCollapsibleSection.vue';
import ApplicationUtils, { hasPasswordSyncSupport } from 'client/applications/utils/ApplicationUtils';
import CheckboxWithLabel from 'client/components/FormValidation/CheckboxWithLabel.vue';
import Constants, { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import HardcodedScimSupportMixin from 'client/applications/mixins/HardcodedScimSupportMixin';
import IdentityManagementMixin, { SCIMPanelEventManager } from 'client/applications/mixins/IdentityManagementMixin';
import IdmPrebuiltCredentialsForm from 'client/applications/scim/IdmPrebuiltCredentialsForm.vue';
import IdmUsersImportFilterSection from 'client/applications/import/IdmUsersImportFilterSection.vue';
import ScimConnectorAttributes from 'client/applications/scim/ScimConnectorAttributes.vue';
import ScimUsersImportSection from 'client/applications/import/ScimUsersImportSection';
import navigationTools from 'client/util/PageNavigationUtil';

const { application } = ItemTypeConsts;
const ApplicationModel = StoreModelMap[application];
const { success, error } = Constants;

const { SCIM } = scimConnectionTypes;

export const formStates = { ACTIVATE: 0, CONFIGURE: 1, UPDATE: 2 };
const { ACTIVATE, CONFIGURE, UPDATE } = formStates;

const ACTION_BUTTON_TEXTS = {
  [ACTIVATE]: 'Activate',
  [CONFIGURE]: 'Configure',
  [UPDATE]: 'Update',
};

export default {
  name: 'PreBuiltIdmConnectorConfig',

  components: {
    ApplicationCollapsibleSection,
    Button,
    ButtonSecondary,
    CheckboxWithLabel,
    HeadingMedium,
    IdmPrebuiltCredentialsForm,
    Link,
    ScimConnectorAttributes,
    ScimUsersImportSection,
    ValidationObserver,
    IdmUsersImportFilterSection,
  },

  mixins: [HardcodedScimSupportMixin, IdentityManagementMixin],

  data() {
    return {
      areCredentialsValid: false,
      baseUrl: '',
      clientId: '',
      clientSecret: '',
      configSettingsExpanded: false,
      isActivationFlow: false,
      loading: false,
      tokenKey: '',
      onActivateState: false,
    };
  },

  computed: {
    ...mapState(ApplicationModel, {
      idField: 'id',
      isNew: 'isItemBeingCreated',
      item: 'item',
      needsVanityUrl: 'scimNeedsVanityUrl',
      template: 'template',
    }),

    ...mapGetters(ApplicationModel, {
      isItemBeingCreated: 'isItemBeingCreated',
    }),

    buttonText() {
      return ACTION_BUTTON_TEXTS[this.formState];
    },

    formState() {
      if (!this.isScimActive && !this.onActivateState) {
        return CONFIGURE;
      }
      return this.onActivateState ? ACTIVATE : UPDATE;
    },
    getIdmDescription() {
      return this.isIdmGenericApplication
        ? 'Use this Identity Management integration to import new users from this application.'
        : `Set up an Identity Management integration using the SCIM standard to provision,
update, deactivate, and import user accounts in this application service provider as
changes are made in JumpCloud.`;
    },

    isIdmGenericApplication() {
      const getApplicationType = this.item?.provision?.type?.split(':')[0];
      return getApplicationType === 'idm';
    },

    learnMoreText() {
      return `Learn more about Identity Management with ${this.item.displayName}`;
    },

    onConfigureState() {
      return this.formState === CONFIGURE;
    },

    /**
     * During Personio 1.1 Epic, hide update button for all prebuilts
     * except Personio
     */
    showActionButton() {
      let output = true;

      // item.provision.type possible values: [idm|scim]:<app name>
      const isIntegrationPrebuiltScim = (this.item.provision?.type.split(':')[0] === SCIM);

      if (this.formState === UPDATE && isIntegrationPrebuiltScim) {
        output = false;
      }
      return output;
    },

    showActivateButton() {
      return this.showActionButton ? this.formState !== UPDATE : false;
    },

    showAttributeMapping() {
      const { name } = this.item;
      return hasPasswordSyncSupport(name);
    },

    showGroupsSupport() {
      const { name } = this.item;
      return this.isGroupsBetaFlagActive(name) || this.isGroupsSupported;
    },

    /**
     * Hide the horizontal rule when manual import button,
     * groups mgmt checkbox, nor the scim credentials form are rendered
     */
    showHorizontalRule() {
      return this.showManualImportButton || this.showGroupsSupport;
    },

    showIdentityManagementPanel() {
      const { name } = this.item;
      const isBetaFeatureActive = this.hasBetaFeature(name) && !this.isBetaflagActive(name);

      if (isBetaFeatureActive) return false;

      const isScimSupported = !!this.item.provision || this.hardcodedIsScimSupported(name);

      return isScimSupported;
    },

    showManualImportButton() {
      return !this.useNewUsersImportFlow && this.hideImport();
    },

    showSettings() {
      return (this.needsCredentials || this.needsSimpleToken) && this.configSettingsExpanded;
    },

    showUpdateButton() {
      return this.showActionButton ? this.formState === UPDATE : false;
    },

    useNewUsersImportFlow() {
      const { importUserUpdatesCustomApi } = this.$featureFlags;

      const isCustomIntegration = !this.template.provision; // Customs don't have this prop set
      const isScimIntegration = isCustomIntegration
        ? (this.scimConnectionType === SCIM) : this.template.provision.type.split(':')[0] === SCIM.toLowerCase();

      return !isScimIntegration && this.$hasFeatureFlag(importUserUpdatesCustomApi);
    },
  },

  methods: {
    ...mapActions('ApplicationModel', {
      setOAuthConfigValue: 'setOAuthConfigValue',
      setScimCredentials: 'setScimCredentials',
      triggerErrorNotification: 'triggerErrorNotification',
      updateIdmOauthConfiguration: 'updateCustomIdmOauthConfiguration',
    }),

    activationFlow() {
      this.setCredentials();

      this.activateScim()
        .then(() => {
          this.hideConfigChevron = false;
          notify(success, applicationMessages.get('idmSuccess'));
          this.handleNewAppIdmActivation();
          this.toggleCollapse();
          this.clearCredentials();
          this.onActivateState = false;
        })
        .catch(() => {
          notify(error, applicationMessages.get('idmError'));
        }).finally(() => {
          this.loading = false;
        });
    },

    clearCredentials() {
      this.baseUrl = '';
      this.clientId = '';
      this.clientSecret = '';
      this.tokenKey = '';
      this.$refs.credentialsForm.reset();
    },

    configurationFlow() {
      if (this.isItemBeingCreated) {
        this.emitScimEvent('showScimModal', {});
        this.loading = false;
      } else {
        this.configureScim()
          .then(() => {
            if (this.redirectLocation !== '') {
              this.loading = true;
              this.handleRedirectConfigure();
            }
            this.onActivateState = true;
            this.configSettingsExpanded = true;
          })
          .catch((responseError) => {
            notify(error, responseError.message);
          }).finally(() => {
            this.loading = false;
          });
      }
    },

    emitScimEvent(event) {
      SCIMPanelEventManager.$emit(event);
    },

    handleActionButton() {
      this.loading = true;
      switch (this.formState) {
        case ACTIVATE:
          this.activationFlow();
          break;

        case UPDATE:
          this.updateFlow();
          break;

        case CONFIGURE:
        default:
          this.configurationFlow();
          break;
      }
    },

    async handleCredentialsChange(key, value) {
      this[key] = value;
    },

    handleNewAppIdmActivation() {
      if (this.isNew) {
        this.emitScimEvent('scimCredentialsValid');
      }
    },

    handleRedirectConfigure() {
      notify(success, 'Redirecting to SP login page');
      this.loading = true;
      navigationTools.setLocation(this.redirectLocation);
    },

    hideImport() {
      const appName = ApplicationUtils.hideManualImportApps().find(
        app => app === this.item.displayName,
      );
      return !appName;
    },

    isAppScimBeta() {
      const appName = this.item.name;
      return this.isBetaScim(appName);
    },

    setCredentials() {
      if (this.needsCredentials) {
        this.setScimCredentials({
          keyValue: this.tokenKey,
          urlValue: this.baseUrl,
        });
      }

      if (this.needsSimpleToken) {
        this.setOAuthConfigValue({
          clientId: this.clientId,
          clientSecret: this.clientSecret,
        });
      }
    },

    toggleCollapse() {
      if (this.isScimActive || this.isActivationFlow) {
        this.configSettingsExpanded = !this.configSettingsExpanded;
      }
    },

    updateFlow() {
      this.setCredentials();
      this.updateIdmOauthConfiguration()
        .then(() => {
          notify(success, applicationMessages.get('idmSuccess'));
          this.toggleCollapse();
          this.clearCredentials();
        })
        .catch(() => {
          notify(error, applicationMessages.get('idmError'));
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style module>
@import '../../css/sections.css';
@import 'css/IdentityManagementCommon.css';

.collapsibleContent {
  border-top: 1px solid var(--jc-color-neutral-stroke);
  padding-top: 1rem;
}

.groups {
  margin-bottom: 20px;
  margin-top: 15px;
}

.infoBetaScim {
  color: gradientDark1;
  font-weight: 400;
  line-height: 15px;
}

.infoContainer svg {
  height: 13px;
  margin-bottom: 3px;
}
</style>
